import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canBookInactiveResourcesAsAdmin } from "./functions/canBookInactiveResourcesAsAdmin.function";
import { buildWorkplaceSeatStatus } from "./functions/buildWorkplaceSeatStatus.function";
import { useLocationAdminLocationIds } from "../../hooks/Remote/User/UserInfo/useLocationAdminLocationIds";
import {
  Grid2,
  Typography,
  Dialog,
  DialogContent,
  Skeleton,
  Box,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { getCurrentFloor } from "../../features/Booking-Form/functions/form.functions";
import {
  hasActiveBookingFilters,
  groupFilterItemsFromBookingFilters,
  filterItemsReflectBookingFilters,
  areBookingFilterCatalogsReady,
  shouldApplyInitialPreferredPlaceCategoryFilter,
  type BookingFilterCatalogs
} from "../../features/Booking-Form/functions/booking-filter-state.functions";
import {
  clearBookingFilters,
  selectSingleWorkplace,
  selectTeamMemberWorkplace,
  setFloors,
  setInputs
} from "../../features/Booking-Form/slices/booking.slice";
import { SelectFloorPlanView } from "../FacilityManager/Components/Views/SelectFloorPlanView/SelectFloorPlanView";
import { IFloorPayload } from "../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import {
  checkActivityBased,
  fetchFloorPlanState,
  applyBookingFilterOnFloorPlan,
  handleFilterSelect,
  updateFilters,
  initCategoryOptions,
  initFilterOptions,
  handleInitialPreferredCategoryFilter
} from "./functions/form.functions";
import { getFormWorkplaceInitialUserIndex } from "./functions/form-workplace-initial-user-index.functions";
import { useBackgroundImage } from "../FacilityManager/Hooks/useBackgroundImage";
import { FormEditTimeDialog } from "./form-edit-time-dialog.component";
import { BookingFilterButtons } from "./BookingFilter/BookingFilterButtonGroup.component";
import { SlideoutPanelDialog } from "../SlideoutPanel/SlideoutPanelDialog.component";
import { FloorFilters } from "./BookingFilter/FloorFilters.component";
import { generateFloorButtonLabel } from "./BookingFilter/functions/FloorFilter.functions";
import { GroupFilters } from "./BookingFilter/GroupFilters.component";
import { ColleagueFilterState, GroupFilterSelected } from "./BookingFilter/types/GroupFilters.type";
import FormWorkplaceTeamBookingChips from "./form-workplace-team-booking-chips.component";
import { ConnectedBookingData } from "../../features/Booking-Form/typings/connected-bookingData";
import FormTeamSelection from "./FormTeamSelection/form-team-selection.component";
import { BookingFilterMode } from "./Domain/booking-filter-mode-enum.type";
import { useRemoteMutateGetBookingStatusInformation } from "../../hooks/Remote/Booking/useRemoteMutateGetBookingStatusInformation";
import { checkWeekdays } from "../FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/SelectionPrompt.functions";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { getPlanDateRangeOrNull } from "../../features/Booking-Form/functions/floor.functions";
import { OccupiedStatus } from "../FacilityManager/Domain/Types/Status";
import { useRemoteMutateFetchFloorPlanService } from "../../hooks/Remote/FloorPlan/useRemoteMutateFetchFloorPlanService";
import { useRemoteFetchBookingFavoritesByFloor } from "../../hooks/Remote/BookingFavorites/useRemoteFetchBookingFavoritesByFloor";
import { isBookingTypeWithFavorites } from "../../hooks/Remote/BookingFavorites/is-booking-type-with-favorites";
import type { BookingFavoriteDto } from "../../hooks/Remote/BookingFavorites/booking-favorites.types";
import {
  getFavoriteInventoryIdsSetForMode,
  getRecommendedFavoriteForMode
} from "./functions/form-workplace-favorites.functions";
import { getUniqueConnectedBookingsForColleagueFilter } from "./functions/form-workplace-connected-booking.functions";
import { shouldSkipFavoriteAutoSelect } from "../FacilityManager/Components/Views/SelectFloorPlanView/Functions/recommendedFavoriteAutoSelect.function";

type P = {
  nextStep: () => void;
};

/**
 * @description Component that displays the location floors and blueprints including the desks and zones.
 * @param props.nextStep as a function which goes to the next step in the booking process.
 * @version 0.1.2
 */
export const FormWorkplace: React.FC<P> = props => {
  const { t } = useTranslation();
  const {
    inputs,
    floors: { inventory, currentFloorIndex, isLoading, error }
  } = useSelector((state: RootState) => state.booking);
  const {
    userInformation,
    settings: { preferredPlaceCategories }
  } = useSelector((state: RootState) => state.login);
  const { isCompanyAdmin, isLocationAdmin } = useSelector(
    (state: RootState) => state.login.userRoles
  );
  const locationAdminLocationIds = useLocationAdminLocationIds();

  const dispatch = useDispatch();

  const theme = useTheme();
  const isBrowser = useMediaQuery(theme.breakpoints.up(1200));

  const [floorPlan, setFloorPlan] = useState<IFloorPayload>();
  const [floorPlanForService, setFloorPlanForService] = useState<IFloorPayload>();
  const [currentUser, setCurrentUser] = useState<number>(
    getFormWorkplaceInitialUserIndex(inputs.mode, inputs.usersBookedFor)
  );

  // filter place/zone by category selection
  const [filteredSelectables, setFilteredSelectables] = useState<number[] | undefined>(undefined);

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isFloorFilterOpen, setIsFloorFilterOpen] = useState(false);
  const [isGroupFilterOpen, setIsGroupFilterOpen] = useState(false);
  const [detailGroupFilter, setDetailGroupFilter] = useState(false);
  const [isTeamBookingOpen, setIsTeamBookingOpen] = useState(false);
  const [colleagueFilterState, setColleagueFilterState] = useState(
    ColleagueFilterState.DEACTIVATED
  );
  const [selectedColleagueFilter, setSelectedColleagueFilter] = useState<ConnectedBookingData[]>(
    []
  );
  const [selectedColleagueFilterPlaces, setSelectedColleagueFilterPlaces] = useState<
    ConnectedBookingData[]
  >([]);
  const [filterItems, setFilterItems] = useState<GroupFilterSelected[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const loaded = useRef<boolean>(false);
  // booking filter mode, seperate intial filter used and further
  const [filterMode, setFilterMode] = useState<BookingFilterMode>(() =>
    hasActiveBookingFilters(inputs.bookingFilters)
      ? BookingFilterMode.FILTERING
      : BookingFilterMode.INITIAL
  );

  // get current floor by currentFloorIndex, initially select the most bookable floor for the first floor
  const currentFloor = getCurrentFloor(
    dispatch,
    inventory,
    currentFloorIndex,
    hasActiveBookingFilters(inputs.bookingFilters)
  );

  const canBookInactive = useMemo(
    () =>
      canBookInactiveResourcesAsAdmin({
        isCompanyAdmin,
        isLocationAdmin,
        bookingType: inputs.bookingType,
        locationInventoryId: inputs.selectedLocation,
        locationAdminLocationIds
      }),
    [
      isCompanyAdmin,
      isLocationAdmin,
      inputs.bookingType,
      inputs.selectedLocation,
      locationAdminLocationIds
    ]
  );

  const floorButtonLabel = useMemo(
    () =>
      generateFloorButtonLabel(inventory, currentFloorIndex, {
        canBookInactive
      }),
    [inventory, currentFloorIndex, canBookInactive]
  );

  const { data: floorFavoritesData } = useRemoteFetchBookingFavoritesByFloor(
    floorPlan?.floorInventoryId != null && isBookingTypeWithFavorites(inputs.mode)
      ? floorPlan.floorInventoryId
      : null
  );

  const favoriteInventoryIds = useMemo(
    () => getFavoriteInventoryIdsSetForMode(floorFavoritesData?.favorites, inputs.mode),
    [floorFavoritesData?.favorites, inputs.mode]
  );

  const recommendedFavoriteForFloor = useMemo(
    (): BookingFavoriteDto | null =>
      getRecommendedFavoriteForMode(floorFavoritesData?.recommendedFavorite, inputs.mode),
    [floorFavoritesData?.recommendedFavorite, inputs.mode]
  );

  const skipFavoriteAutoSelect = useMemo(
    () => shouldSkipFavoriteAutoSelect(filteredSelectables),
    [filteredSelectables]
  );

  // download and set the background image here
  const { background, unsetBackgroundImage, isEmptyBgUsed } = useBackgroundImage({
    backgroundImageUrl: floorPlan?.outlineUrl,
    viewport: floorPlan?.viewport
  });

  // initiate place/zone category options
  const usedCategory = initCategoryOptions(inputs.mode, floorPlan, currentFloor);
  // initiate equipment categories and booking properties options
  const usedOptions = initFilterOptions(inputs.mode, floorPlan);

  const isFilterEntryEmpty =
    usedCategory.options.length === 0 &&
    usedOptions?.usedBookingProperties.length === 0 &&
    usedOptions.usedEquipmentCategories.length === 0 &&
    usedOptions.usedEquipment.length === 0;

  const bookingFilterCatalogs = useMemo((): BookingFilterCatalogs => {
    return {
      placeCategories: usedCategory.type === "place" ? usedCategory.options : [],
      zoneCategories: usedCategory.type === "zone" ? usedCategory.options : [],
      bookingProperties: usedOptions?.usedBookingProperties,
      equipmentCategories: usedOptions?.usedEquipmentCategories,
      equipment: usedOptions?.usedEquipment
    };
  }, [usedCategory, usedOptions]);

  const syncFilterItemsFromBookingFilters = useCallback(() => {
    if (!hasActiveBookingFilters(inputs.bookingFilters)) {
      return;
    }

    if (!areBookingFilterCatalogsReady(inputs.bookingFilters!, usedCategory)) {
      return;
    }

    if (filterItemsReflectBookingFilters(filterItems, inputs.bookingFilters!)) {
      return;
    }

    setFilterItems(
      groupFilterItemsFromBookingFilters(inputs.bookingFilters!, bookingFilterCatalogs)
    );
    setFilterMode(BookingFilterMode.FILTERING);
  }, [bookingFilterCatalogs, filterItems, inputs.bookingFilters, usedCategory]);

  const openGroupFilterPanel = useCallback(() => {
    syncFilterItemsFromBookingFilters();
    setIsGroupFilterOpen(true);
  }, [syncFilterItemsFromBookingFilters]);

  useEffect(() => {
    if (isGroupFilterOpen) {
      return;
    }

    if (!floorPlan || !hasActiveBookingFilters(inputs.bookingFilters)) {
      return;
    }

    if (!areBookingFilterCatalogsReady(inputs.bookingFilters!, usedCategory)) {
      return;
    }

    if (filterItemsReflectBookingFilters(filterItems, inputs.bookingFilters!)) {
      if (filterMode !== BookingFilterMode.FILTERING) {
        setFilterMode(BookingFilterMode.FILTERING);
      }
      return;
    }

    setFilterItems(
      groupFilterItemsFromBookingFilters(inputs.bookingFilters!, bookingFilterCatalogs)
    );
    setFilterMode(BookingFilterMode.FILTERING);
  }, [
    floorPlan,
    inputs.bookingFilters,
    bookingFilterCatalogs,
    usedCategory,
    currentFloor?.id,
    isGroupFilterOpen,
    filterItems,
    filterMode
  ]);

  // set the data about the occupied booking / booking conflicts
  const [occupiedBookingData, setOccupiedBookingData] = useState<OccupiedStatus[]>([]);
  const { mutateAsync: getBookingStatusInfo, isLoading: isOccupiedDataLoading } =
    useRemoteMutateGetBookingStatusInformation();

  // to find the assigned service items in the service plan
  const { mutateAsync: fetchFloorPlanForService } = useRemoteMutateFetchFloorPlanService();

  /** fetch booking status data and then open time edit dialog */
  const getOccupiedBookingData = (selectedEntryId: number) => {
    getBookingStatusInfo({
      caseDTO: {
        bookingInventoryIds: [selectedEntryId],
        startDate: inputs.bookingFrom,
        endDate: inputs.bookingTo,
        startTime: "00:00", // inputs.bookingStart,
        endTime: "23:59", // inputs.bookingEnd,
        bookingType: inputs.bookingType as BookingType,
        timezone: inputs.timezone,
        weekdays: checkWeekdays(inputs.weekdays)
      }
    }).then(data => {
      setOccupiedBookingData(data.status.flatMap(stat => stat.occupiedStatus));
      setIsDateOpen(true);

      dispatch(setInputs({ bookingDays: data.bookingDays }));
    });
  };

  const toggleColleagueFilterState = () => {
    switch (colleagueFilterState) {
      case ColleagueFilterState.DEACTIVATED:
        return ColleagueFilterState.FILLED;
      case ColleagueFilterState.FILLED:
        return ColleagueFilterState.EMPTY;
      case ColleagueFilterState.EMPTY:
        return ColleagueFilterState.DEACTIVATED;
    }
  };

  useEffect(() => {
    setCurrentUser(getFormWorkplaceInitialUserIndex(inputs.mode, inputs.usersBookedFor));
  }, [inputs.mode, inputs.usersBookedFor]);

  useEffect(() => {
    setFiltersChanged(true);
  }, [inputs.bookingFilters, inputs.floorInventoryId]);

  useEffect(() => {
    checkActivityBased(inputs.activityBasedBooking, props.nextStep);
  }, [inputs.activityBasedBooking, props.nextStep]);

  useEffect(() => {
    updateFilters(inputs, currentUser, loaded, dispatch, setFiltersChanged, filtersChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputs.bookingEnd,
    inputs.bookingFrom,
    inputs.bookingStart,
    inputs.bookingTo,
    inputs.bookingType,
    inputs.selectedLocation,
    inputs.usersBookedFor,
    inputs.bookingFilters,
    inputs.floorInventoryId,
    inputs.weekdays,
    inputs.mode,
    inputs.frequence,
    inputs.interval,
    inputs.specificDays,
    inputs.bookingMonthDay,
    inputs.bookingYearDay,
    filtersChanged,
    currentUser
  ]);

  useEffect(() => {
    fetchFloorPlanState(inputs, setFloorPlan, currentFloor);

    const isEditingService =
      inputs.mode === BookingType.CONFERENCEZONE &&
      userInformation.company.meta.activateBookingServices;

    const planRangeForServices = getPlanDateRangeOrNull(
      inputs.bookingFrom,
      inputs.bookingStart,
      inputs.bookingTo,
      inputs.bookingEnd
    );
    if (isEditingService && currentFloor?.id && planRangeForServices) {
      fetchFloorPlanForService({
        floorInventoryId: currentFloor.id,
        dto: { end: planRangeForServices.end, start: planRangeForServices.start }
      }).then(data => {
        if (data) {
          setFloorPlanForService(data);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputs.automatedSeating,
    inputs.bookingEnd,
    inputs.bookingFrom,
    inputs.bookingStart,
    inputs.bookingTo,
    inputs.activityBasedBooking,
    inputs.isMobileWorking,
    currentFloor?.id
  ]);

  // Apply preferred place category on floor plan only when the early filter step is not shown.
  useEffect(() => {
    if (
      !floorPlan ||
      !shouldApplyInitialPreferredPlaceCategoryFilter(
        inputs,
        hasActiveBookingFilters(inputs.bookingFilters)
      )
    ) {
      return;
    }

    handleInitialPreferredCategoryFilter({
      preferredPlaceCategories,
      floorPlan,
      usedCategory,
      filterMode,
      isGroupFilterOpen,
      setFilteredSelectables,
      setFilterItems
    });
  }, [floorPlan, currentFloorIndex, isGroupFilterOpen, inputs.bookingFilters, inputs]);

  // apply booking group filter on the floor plan
  useEffect(() => {
    /* istanbul ignore next */
    if (floorPlan) {
      applyBookingFilterOnFloorPlan(
        inputs,
        floorPlan,
        filterItems,
        filterMode,
        setFilteredSelectables
      );
    }
  }, [inputs.bookingFilters, floorPlan, filterItems, currentFloorIndex, currentUser, filterMode]);

  //Get all places the selected colleagues have booked to display the initials in all places
  useEffect(() => {
    setSelectedColleagueFilterPlaces(
      getUniqueConnectedBookingsForColleagueFilter(selectedColleagueFilter, currentFloor)
    );
  }, [selectedColleagueFilter, currentFloor]);

  /**
   * @deprecated it used to be that if current floor has no empty workplaces select the next floor.
   * selectWorkplaceFloor(inventory, dispatch, currentFloorIndex);
   */

  if (error)
    return <Typography variant={"h5"}>{t("There was an extreme error getting floors")}</Typography>;

  return (
    <>
      <Grid2 data-testid="form-booking-parent" container>
        <Grid2
          data-testid="form-booking-select-parent"
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            alignItems: "flex-end",
            display: "inline-block"
          }}
        >
          {/* new booking filter buttons */}
          <BookingFilterButtons
            bookingMode={inputs.mode}
            floorAvailability={inventory[currentFloorIndex ?? 0]}
            floorButtonLabel={floorButtonLabel}
            colleagueFilterState={colleagueFilterState}
            setIsFloorFilterOpen={() => setIsFloorFilterOpen(true)}
            setIsGroupFilterOpen={openGroupFilterPanel}
            setColleagueFilterState={() => setColleagueFilterState(toggleColleagueFilterState())}
            setIsDateOpen={() => setIsDateOpen(true)}
            setIsTeamBookingOpen={setIsTeamBookingOpen}
            filterItems={filterItems}
            selectedColleagueFilter={selectedColleagueFilter}
            setSelectedColleagueFilter={setSelectedColleagueFilter}
            isFilterEntryEmpty={isFilterEntryEmpty}
            isFilterAble={!!floorPlan && !isLoading && !!background}
          />

          {/* floor filter slideout panel */}
          <SlideoutPanelDialog
            isOpen={isFloorFilterOpen}
            dialogTitle={t("Floor")}
            width="20vw"
            showCloseButton
            onClose={() => setIsFloorFilterOpen(false)}
          >
            <FloorFilters
              floorInventory={inventory}
              currentFloor={currentFloor}
              allowInactiveResourceBooking={canBookInactive}
              onSelectFloor={(id, index) => {
                dispatch(setInputs({ floorInventoryId: id }));
                dispatch(setFloors({ currentFloorIndex: index }));
                unsetBackgroundImage();
                setIsFloorFilterOpen(false);
                if (hasActiveBookingFilters(inputs.bookingFilters)) {
                  setFilterMode(BookingFilterMode.FILTERING);
                } else {
                  setFilterMode(BookingFilterMode.INITIAL);
                }
              }}
            />
          </SlideoutPanelDialog>

          {/* group filter slideout panel */}
          <SlideoutPanelDialog
            isOpen={isGroupFilterOpen}
            dialogTitle={t("Filter")}
            width={detailGroupFilter ? "35vw" : "25vw"}
            showCloseButton
            showDetailToggle
            detailGroupFilterToggle={
              <FormControlLabel
                sx={{ width: "100%" }}
                control={
                  <Switch
                    data-testid="show-detail-toggle"
                    value={detailGroupFilter}
                    onChange={() => setDetailGroupFilter(!detailGroupFilter)}
                  />
                }
                label={detailGroupFilter ? t("Show details") : t("Hide details")}
                slotProps={{ typography: { sx: { width: "fit-content" } } }}
              />
            }
            onClose={() => {
              setIsGroupFilterOpen(false);
              setDetailGroupFilter(false);
            }}
            primaryButtonTitle={t("Apply")}
            secondaryButtonTitle={t("Reset")}
            primaryButtonAction={() => {
              if (
                filterMode === BookingFilterMode.INITIAL ||
                filterMode === BookingFilterMode.RESET
              ) {
                setFilterMode(BookingFilterMode.FILTERING);
              }
              handleFilterSelect(filterItems, inputs, currentUser, dispatch);
            }}
            secondaryButtonAction={() => {
              setFilterItems([]);
              setFilteredSelectables(undefined);
              setFilterMode(BookingFilterMode.RESET);
              dispatch(clearBookingFilters());
              if (inputs.usersBookedFor?.[currentUser]) {
                const newTeam = [...inputs.usersBookedFor];
                newTeam[currentUser] = {
                  ...newTeam[currentUser],
                  bookingFilters: undefined
                };
                dispatch(setInputs({ usersBookedFor: newTeam }));
              }
            }}
          >
            <GroupFilters
              floorPlan={floorPlan}
              showDetailGroupFilter={detailGroupFilter}
              filterItems={filterItems}
              setFilterItems={setFilterItems}
              usedCategory={usedCategory}
              usedOptions={usedOptions}
              filterMode={filterMode}
              isCapacityReportUsed={false}
            />
          </SlideoutPanelDialog>
        </Grid2>

        {/* team booking mode - chips of booking member */}
        <FormWorkplaceTeamBookingChips
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          inputs={inputs}
        />

        {/* floor plan part */}
        {(isLoading || !background) && (
          <Skeleton variant="rectangular" sx={{ width: "100%", height: "calc(100vh - 250px)" }} />
        )}
        {!isLoading && inputs.usersBookedFor && currentFloor && floorPlan && background && (
          <Box
            data-testid="select-floorplanview-parent"
            id="select-floor-parent"
            sx={{ width: "100%", height: "calc(85vh - 250px)" }}
          >
            <SelectFloorPlanView
              background={background}
              isEmptyBgUsed={isEmptyBgUsed}
              name={"booking"}
              allowInactiveResourceBooking={canBookInactive}
              disabledInventoryIdsForAdminSelection={
                canBookInactive ? currentFloor.disabledInventoryIds : undefined
              }
              floorPlan={floorPlan}
              floorPlanForService={floorPlanForService}
              seatStatus={buildWorkplaceSeatStatus(
                currentFloor,
                filteredSelectables,
                canBookInactive
              )}
              userOccupiedPlaces={currentFloor.userOccupiedInventoryIds ?? []}
              bookingInputs={{
                mode: inputs.mode,
                bookingFrom: inputs.bookingFrom,
                bookingTo: inputs.bookingTo,
                bookingStart: inputs.bookingStart,
                bookingEnd: inputs.bookingEnd,
                bookingType: inputs.bookingType,
                usersBookedFor: inputs.usersBookedFor,
                timezone: inputs.timezone,
                weekdays: inputs.weekdays,
                zoneAccess: inputs.zoneAccess,
                selectedLocation: inputs.selectedLocation,
                selectedFloor: currentFloor.id
              }}
              userIndex={currentUser}
              setUserIndex={(i: number) => setCurrentUser(i)}
              warningData={{
                requiredUserWarningData: currentFloor.requiredUserWarningData,
                optionalUserWarningData: currentFloor.optionalUserWarningData
              }}
              onSingleSelect={(placeInventoryId: number) => {
                dispatch(
                  selectSingleWorkplace({
                    firstName: userInformation.firstName,
                    surname: userInformation.surname,
                    userId: userInformation.sub,
                    bookingInventoryId: placeInventoryId,
                    email: userInformation.email,
                    isExternal: false
                  })
                );
                props.nextStep();
              }}
              onTeamSelect={(index: number, placeInventoryId: number) => {
                dispatch(selectTeamMemberWorkplace({ index, placeInventoryId: placeInventoryId }));
              }}
              onNextStep={props.nextStep}
              isOccupiedDataLoading={isOccupiedDataLoading}
              openTimeDialog={getOccupiedBookingData}
              selectedColleagueFilter={
                colleagueFilterState === ColleagueFilterState.DEACTIVATED
                  ? []
                  : selectedColleagueFilterPlaces
              }
              favoriteInventoryIds={favoriteInventoryIds}
              recommendedFavorite={recommendedFavoriteForFloor}
              skipFavoriteAutoSelect={skipFavoriteAutoSelect}
            />
          </Box>
        )}
      </Grid2>
      {/* change time dialog */}
      <Dialog
        data-testid="dialog-date-open"
        onClose={() => setIsDateOpen(false)}
        aria-labelledby="simple-dialog-title"
        open={isDateOpen}
        slotProps={{ paper: { sx: { maxHeight: "83vh" } } }}
        maxWidth={isBrowser ? false : "md"}
        fullWidth={!isBrowser}
      >
        <DialogContent sx={[{ p: 2 }, isBrowser ? { minWidth: 500 } : { minWidth: "fit-content" }]}>
          <FormEditTimeDialog
            inputs={inputs}
            occupiedBookingData={occupiedBookingData}
            setIsDateOpen={setIsDateOpen}
          />
        </DialogContent>
      </Dialog>
      {/* change team booking member dialog */}
      <Dialog
        data-testid="dialog-team-open"
        onClose={() => setIsTeamBookingOpen(false)}
        aria-labelledby="simple-dialog-title"
        open={isTeamBookingOpen}
      >
        <DialogContent data-testid="dialog-team-content">
          <FormTeamSelection title={t("Edit your team selection")} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FormWorkplace;
